import {
  SelectionTree,
  TreeNodeDefinition,
} from '@trimble/ui-components/solidjs';
import { t } from '@trimble/i18n';
import { readModusInputChangeValue } from '@trimble/utilities';
import { Component, JSX, Show } from 'solid-js';
import { Portal } from 'solid-js/web';
import type { MeasurementSurface } from '../../types/surface';

export interface SurfaceSelectComboboxViewProps {
  testId: string;
  label: string;
  disabled?: boolean;
  isOpen: boolean;
  filterText: string;
  selectedDisplayName: string;
  dropdownId: string;
  panelStyle: JSX.CSSProperties;
  treeNodes: TreeNodeDefinition<MeasurementSurface>[];
  defaultExpandIds: string[];
  selectedSurfaceId?: string;
  setRootRef(el: HTMLDivElement): void;
  setPanelRef(el: HTMLElement | null): void;
  onTriggerActivate(): void;
  onFilterTextChange(value: string): void;
  onNodeSelect(
    nodeId: string,
    node: TreeNodeDefinition<MeasurementSurface>
  ): void;
}

const noop = (): void => undefined;

export const SurfaceSelectComboboxView: Component<
  SurfaceSelectComboboxViewProps
> = (props) => {
  const inputValue = (): string =>
    props.isOpen ? props.filterText : props.selectedDisplayName;

  const openIfNeeded = (): void => {
    if (props.disabled) {
      return;
    }
    props.onTriggerActivate();
  };

  return (
    <div
      ref={props.setRootRef}
      class="relative w-full"
      data-testid={props.testId}
    >
      <div
        role="combobox"
        aria-expanded={props.isOpen}
        aria-haspopup="listbox"
        aria-disabled={props.disabled}
        aria-label={props.label}
        data-testid={`${props.testId}-trigger`}
      >
        <modus-wc-text-input
          class="[&_input]:!opacity-100 [&_.modus-wc-input-label]:!mb-0"
          label={props.label}
          value={inputValue()}
          placeholder={t('general.search')}
          bordered={true}
          size="md"
          type="search"
          auto-complete="off"
          disabled={props.disabled}
          include-search={true}
          include-clear={props.isOpen}
          onClick={openIfNeeded}
          on:inputFocus={openIfNeeded}
          on:inputChange={(event) => {
            props.onFilterTextChange(readModusInputChangeValue(event));
          }}
        />
      </div>

      <Show when={props.isOpen}>
        <Portal>
          <section
            class="fixed z-dropdown pointer-events-auto rounded border border-[var(--modus-wc-color-base-200,#e0e1e9)] bg-[var(--modus-wc-color-base-page,#fff)] shadow-md"
            style={props.panelStyle}
            data-testid={`${props.testId}-menu`}
            data-dropdown-id={props.dropdownId}
            ref={props.setPanelRef}
            role="listbox"
            aria-label={props.label}
          >
            <div
              class="max-h-64 overflow-x-hidden overflow-y-auto overscroll-contain p-2"
              data-testid={`${props.testId}-tree`}
            >
              <SelectionTree
                multiSelect={false}
                nodes={props.treeNodes}
                childrenLoadStatus="success"
                defaultExpandIds={props.defaultExpandIds}
                filterText={props.filterText}
                isNodeLoading={() => false}
                isNodeVisible={() => false}
                onVisibilityToggle={noop}
                isNodeSelected={(nodeId) => nodeId === props.selectedSurfaceId}
                onNodeSelect={props.onNodeSelect}
              />
            </div>
          </section>
        </Portal>
      </Show>
    </div>
  );
};
